import type { Route } from './+types/_index';
import { AppraisalCard } from '@/components/sections/appraisal-card';
import { CtaBanner } from '@/components/sections/cta-banner';
import { CtaLinkButton } from '@/components/sections/cta-link';
import { ImageCardGrid } from '@/components/sections/image-card';
import { MarketStats } from '@/components/sections/market-stats';
import { PropertyPaths } from '@/components/sections/property-paths';
import { SectionHeader } from '@/components/sections/section-header';
import { StatCard } from '@/components/sections/stat-card';
import { inheritRootLdJson } from '@/utils/schema';

export function meta({ matches }: Route.MetaArgs) {
  return [
    ...inheritRootLdJson(matches),
    { title: 'Meet Real Estate | Boutique Property Advisory — Kingaroy & South Burnett' },
    {
      name: 'description',
      content:
        'Meet Real Estate is a boutique property advisory serving Kingaroy and the South Burnett, built on 50 years of family heritage and 26 years of global real estate expertise.',
    },
  ];
}

export default function Home() {
  return (
    <main>
      <section className="hero pt-32 pb-20 md:pt-40">
        <div className="hero__content container">
          <p className="hero__tagline text-eyebrow">
            <span>We turn up, when others don&rsquo;t</span>
          </p>

          <div className="grid gap-10 lg:grid-cols-[3fr_2fr] lg:items-end">
            <h1 className="text-display max-w-3xl">We show up to serve.</h1>
            <p className="text-lead lg:justify-self-end lg:max-w-md">
              Buying, selling, renting, or not sure yet — we meet you where you are. Boutique property advice for
              Kingaroy and the South Burnett, from one agent, start to finish.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-[380px_1fr]">
            <div className="flex flex-col gap-6">
              <StatCard
                value="26"
                label="Years"
                description="of real estate experience. Wherever you are on your property journey, we show up to serve."
              />

              <AppraisalCard
                heading="Find out what your home is worth."
                description="A considered appraisal of your property. Start the process here."
                cta={{ label: 'Request an appraisal', to: '/appraisal' }}
                callLink={{ label: 'Or call 0493 035 628', to: 'tel:0493035628' }}
              />
            </div>

            <div className="relative min-h-[28rem] overflow-hidden rounded-md">
              <img
                src="/static/home/hero-verandah.png"
                alt="Queenslander verandah overlooking South Burnett paddocks at golden hour"
                className="absolute inset-0 size-full object-cover"
              />
              {/* Slight brand-gold wash over the stock photo */}
              <div className="absolute inset-0 bg-gradient-to-t from-gold/25 via-gold/0 to-navy/15" />
            </div>
          </div>
        </div>
      </section>

      <MarketStats
        eyebrow="Local market"
        heading="The South Burnett market, in numbers."
        description="Real figures for Kingaroy and the South Burnett — the numbers behind why people are buying, selling, and moving here."
        stats={[
          {
            value: 550000,
            prefix: '$',
            label: 'Median house price',
            caption: 'Kingaroy houses, up around 20% over the past year.',
          },
          {
            value: 22,
            suffix: ' days',
            label: 'Average time on market',
            caption: 'How long a well-priced Kingaroy house takes to sell.',
          },
          {
            value: 1.2,
            decimals: 1,
            suffix: '%',
            label: 'Rental vacancy rate',
            caption: 'A tight market, well under the 3% "balanced" benchmark.',
          },
        ]}
        sourceNote="Sourced from realestate.com.au-derived Kingaroy (4610) suburb data. Current as of mid-2026 — update when fresher figures are published."
      />

      <PropertyPaths
        id="services"
        className="section--light scroll-mt-20"
        eyebrow="Where you are"
        heading="Wherever you are on your property journey."
        description="Four ways people arrive here. All of them start with the same conversation, and none of them start with a contract."
        items={[
          {
            number: '01',
            title: 'Selling',
            description: 'An appraisal, a plan, and one agent handling every inspection personally.',
            href: '/selling',
            linkLabel: 'Discover your selling strategy',
            variant: 'dark',
          },
          {
            number: '02',
            title: 'Buying',
            description:
              "Buyer's agent services for people moving into the region, and for locals who'd rather not do it alone.",
            href: '#contact',
            linkLabel: 'Talk it through',
          },
          {
            number: '03',
            title: 'Property management',
            description: 'For owners who want to know the name of the person looking after their property.',
            href: '#contact',
            linkLabel: 'Ask about management',
          },
          {
            number: '04',
            title: 'Not sure yet',
            description: 'Most people start here. Ask a question, get a straight answer, decide in your own time.',
            href: '#contact',
            linkLabel: 'Ask a question',
          },
        ]}
      />

      {/* pb-0 + the portrait's absolute/bottom-0 below let him bleed flush to
          this section's true bottom edge at every breakpoint — `.section`'s
          own bottom padding would otherwise hold him back from it. */}
      <section id="our-story" className="section section--navy-mid scroll-mt-20 pb-0">
        <div className="container grid gap-16 lg:grid-cols-[1fr_460px]">
          <div className="pb-8 lg:pb-16">
            <SectionHeader
              eyebrow="Our Story"
              heading="From South America to South Burnett."
              align="left"
              className="mb-8"
            />

            <div className="max-w-2xl space-y-4">
              {[
                'Lucio Passos began his real estate career at 17, going on to spend 26 years across three international markets — including six years at Latin America’s largest real estate agency and seven years running his own — before moving to Australia in 2022.',
                'There he met Maria Fernanda, the love of his life. Her 17+ years’ experience as a qualified accountant made her a natural complement to his sales expertise — together they founded Meet Real Estate, bringing a calm, personal approach to Kingaroy and the South Burnett, guided by one principle: we show up to serve.',
              ].map((paragraph) => (
                <p key={paragraph} className="text-body">
                  {paragraph}
                </p>
              ))}
            </div>

            <CtaLinkButton cta={{ label: 'Read our full story', to: '/about#story' }} variant="rule" className="mt-6" />
          </div>

          {/* Mobile: last item in the single-column flow, so with the
              section's own bottom padding zeroed (pb-0 above) his normal
              flow bottom edge already lands flush on the section's true
              bottom edge — no absolute positioning needed here. */}
          <img
            src="/static/home/lucio-portrait.png"
            alt="Lucio Passos"
            className="mx-auto block h-[22rem] w-auto lg:hidden"
          />

          {/* Desktop: this grid cell — not the outer .container — is the
              positioning context for the portrait and its backdrop circle, so
              `right-0`/`bottom-0` land on the padded content edge (matching the
              nav) and the section's true bottom edge, rather than the
              container's own unpadded border box. Needed here (unlike mobile)
              because this cell sits beside a taller text column, so its own
              flow height alone wouldn't reach the row's/section's bottom —
              grid's default stretch gives the cell the full row height, and
              the portrait then escapes down to the very edge of it. */}
          <div className="relative hidden lg:block">
            {/* Cream backdrop circle — gives the cut-out some contrast against
                the flat navy instead of him just floating on it. */}
            <div aria-hidden className="bg-gold-pale absolute right-6 bottom-0 h-[26rem] w-[26rem] rounded-full" />

            {/* Frameless cut-out per brand spec — bleeds flush to the section's
                true bottom edge, no border/shadow. */}
            <img
              src="/static/home/lucio-portrait.png"
              alt="Lucio Passos"
              className="absolute right-0 bottom-0 z-10 h-[35rem] w-auto"
            />
          </div>
        </div>
      </section>

      <ImageCardGrid
        id="insights"
        className="section--light scroll-mt-20"
        eyebrow="Insights"
        heading="What sellers here actually ask."
        linkLabel="All insights"
        linkHref="#insights"
        items={[
          {
            id: 'appraisal-guide',
            image: '/static/insights/south-burnett-appraisal.png',
            eyebrow: 'Appraisals',
            readTime: '6 min read',
            title: 'What a South Burnett appraisal actually involves',
            description:
              'Comparable sales, land size, condition, and the three local factors that move a Kingaroy number more than any portal estimate can see.',
            href: '#insights',
            linkLabel: 'Read the article',
          },
          {
            id: 'five-weeks-before-listing',
            image: '/static/insights/five-weeks-before-listing.webp',
            eyebrow: 'Selling',
            readTime: '4 min',
            title: 'The five weeks before you list',
            description: "What's worth fixing, what isn't, and the order to do it in.",
            href: '#insights',
          },
          {
            id: 'selling-acreage',
            image: '/static/insights/selling-acreage.png',
            eyebrow: 'Rural',
            readTime: '7 min',
            title: "Selling acreage isn't selling a house",
            description: 'Water, access, zoning and the buyer pool that comes with them.',
            href: '#insights',
          },
        ]}
      />

      <CtaBanner
        id="contact"
        className="scroll-mt-20"
        eyebrow="Contact"
        heading="Let's talk."
        description="Buying, selling, renting, or weighing it up for next year."
        primaryCta={{ label: 'Request an appraisal', to: '/appraisal' }}
        secondaryCta={{ label: 'Call Lucio', to: 'tel:0493035628' }}
        contactName="Lucio Passos"
        contactTitle="Sales Agent, Meet Real Estate"
        contactLinks={[
          { label: 'Mobile', value: '0493 035 628', href: 'tel:0493035628' },
          { label: 'Office', value: '07 3355 7777', href: 'tel:0733557777' },
          { label: 'Email', value: 'lucio@meetrealestate.com.au', href: 'mailto:lucio@meetrealestate.com.au' },
          { label: 'Website', value: 'meetrealestate.com.au', href: 'https://meetrealestate.com.au' },
        ]}
      />
    </main>
  );
}
